import { useState } from 'react';
import { Form } from 'react-bootstrap';
import { Activity, ChevronRight, Map } from 'react-feather';
import { useHistory } from 'react-router-dom';

export type Theme = { label: string; count: number; color: string };

/*
 * Explore and Chat are the two features Jumble is actually unusual for, and
 * both used to be a nav link with nothing behind it until you clicked. These
 * two cards show what is already known about your journal (themes come from
 * nuggets, which the context has fetched anyway) and let you start a chat with
 * a real question instead of an empty box.
 */

const CardShell = (props: { icon: JSX.Element; title: string; subtitle: string; children: React.ReactNode }) => {
  return (
    <div className="tw-flex tw-flex-col tw-gap-4 tw-rounded-3xl tw-border-2 tw-border-solid tw-border-neutral-100 tw-bg-white tw-p-5 lg:tw-p-6">
      <div className="tw-flex tw-items-center tw-gap-2">
        <span className="tw-text-primary-500">{props.icon}</span>
        <h3 className="tw-m-0 tw-text-lg tw-font-semibold tw-text-neutral-800">{props.title}</h3>
      </div>
      <div className="tw-text-sm tw-text-neutral-500">{props.subtitle}</div>
      {props.children}
    </div>
  );
};

const ExploreCard = (props: { themes: Theme[] }) => {
  const { themes } = props;
  const history = useHistory();
  const max = Math.max(...themes.map((theme) => theme.count), 1);

  return (
    <CardShell
      icon={<Map className="tw-w-5 tw-h-5" />}
      title="Explore your journal"
      subtitle="What you’ve been writing about"
    >
      {themes.length === 0 ? (
        <div className="tw-text-base tw-text-neutral-600">
          Write a few more entries and Jumble will start pulling out the themes it finds.
        </div>
      ) : (
        <div className="tw-flex tw-flex-col tw-gap-3">
          {themes.map((theme) => (
            <div key={theme.label} className="tw-flex tw-items-center tw-gap-3">
              <div className="tw-w-24 tw-shrink-0 tw-text-sm tw-text-neutral-700 tw-truncate">{theme.label}</div>
              <div className="tw-h-3 tw-flex-1 tw-rounded tw-bg-neutral-100">
                <div
                  className="tw-h-3 tw-rounded"
                  style={{ width: `${Math.round((theme.count / max) * 100)}%`, backgroundColor: theme.color }}
                ></div>
              </div>
              <div className="tw-w-8 tw-shrink-0 tw-text-right tw-text-sm tw-text-neutral-500">{theme.count}</div>
            </div>
          ))}
        </div>
      )}
      <button
        onClick={() => history.push('/retrospective')}
        className="tw-mt-auto tw-flex tw-items-center tw-gap-1 tw-self-start tw-border-none tw-bg-transparent tw-p-0 tw-text-sm tw-font-semibold tw-text-primary-600 tw-cursor-pointer"
      >
        See the full map <ChevronRight className="tw-w-4 tw-h-4" />
      </button>
    </CardShell>
  );
};

const ChatCard = (props: { suggestedQuestions: string[]; onAsk: (question: string) => void }) => {
  const { suggestedQuestions, onAsk } = props;
  const [question, setQuestion] = useState('');

  return (
    <CardShell
      icon={<Activity className="tw-w-5 tw-h-5" />}
      title="Chat with your journal"
      subtitle="Ask a question — answers cite the entries they came from"
    >
      <div className="tw-flex tw-flex-col tw-gap-2">
        {suggestedQuestions.map((suggestion) => (
          <button
            key={suggestion}
            onClick={() => onAsk(suggestion)}
            className="tw-rounded-lg tw-border-none tw-bg-primary-50 tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-text-neutral-700 tw-cursor-pointer hover:tw-bg-primary-100"
          >
            {suggestion}
          </button>
        ))}
      </div>
      <div className="tw-mt-auto tw-flex tw-gap-2">
        <Form.Control
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && question) {
              onAsk(question);
            }
          }}
          className="tw-flex-1 tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-px-3 tw-min-h-control tw-text-base"
          placeholder="Ask your journal..."
        />
        <button
          disabled={!question}
          onClick={() => onAsk(question)}
          className="tw-flex tw-items-center tw-justify-center tw-px-5 tw-min-h-control tw-rounded-lg tw-border-none tw-bg-gradient-to-b tw-from-primary-400 tw-to-secondary-500 tw-text-white tw-text-base tw-font-semibold tw-cursor-pointer disabled:tw-cursor-default disabled:tw-bg-none disabled:tw-bg-neutral-200 disabled:tw-text-neutral-600"
        >
          Ask
        </button>
      </div>
    </CardShell>
  );
};

export const JournalPreviews = (props: {
  themes: Theme[];
  suggestedQuestions: string[];
  onAsk: (question: string) => void;
}) => {
  return (
    <section className="tw-grid tw-gap-5 lg:tw-grid-cols-2">
      <ExploreCard themes={props.themes} />
      <ChatCard suggestedQuestions={props.suggestedQuestions} onAsk={props.onAsk} />
    </section>
  );
};
